import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from "@material-ui/core";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import {
  ActionLink,
  AvailabilityReviewRating,
  CloseButtonIcon,
  Icon,
  IconName,
  MobilePopoverCard,
  MobileSearchFieldButton,
} from "halifax";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Category,
  ExperiencesAvailabilityAutocompleteResult,
  ExperienceTag,
  ExperienceTagCount,
  IResult,
  ParsedGroupedExperienceIds,
} from "redmond";
import { getExperiencesShopQuery, useIsTravelApp } from "@capone/common";

import { MobileKeywordSearchConnectorProps } from "./container";
import "./styles.scss";
import { PATH_SHOP } from "../../../../utils/paths";
import { SEARCH_BY, SEARCH_EXPERIENCES } from "../KeywordSearch/constants";
import Logger from "../../../../utils/logger";
import { getSelectionId } from "../../../../utils/selectionHelpers";
import { useHistory } from "react-router-dom";
import { useSecondarySearchAnalytics } from "../../../../hooks/useSecondarySearchAnalytics";

type CategoryType<ValueType> = {
  results: ValueType[];
  label: string | JSX.Element;
  category: string;
};

export interface IMobileAutocompleteOptionsProps<ValueType> {
  valueCategories: CategoryType<ValueType>[] | null;
  loading?: boolean;
  setSelectedGroupedExperience: (option: ExperienceTagCount) => void;
  fromDate: Date | null;
  untilDate: Date | null;
  experiencesByCategory: ParsedGroupedExperienceIds[];
  categoryTags: ExperienceTagCount[];
  attractionTags?: ExperienceTagCount[];
  location: IResult | null;
  isTravelApp?: boolean;
  setFilterTags?: (tags: ExperienceTag[]) => void;
  onOptionSelect?: () => void;
  onResultClicked?: (categoryLabel: string, resultIndex: number) => void;
}

export const MobileAvailabilityAutocompleteOptions = <
  ValueType extends ExperiencesAvailabilityAutocompleteResult & {
    categoryLabel: string;
  },
>(
  props: IMobileAutocompleteOptionsProps<ValueType>,
): JSX.Element => {
  const {
    loading,
    valueCategories,
    setSelectedGroupedExperience,
    fromDate,
    untilDate,
    categoryTags,
    attractionTags,
    location,
    isTravelApp,
    setFilterTags,
    onOptionSelect,
    onResultClicked,
  } = props;

  const history = useHistory();
  const getGroupedExperience = (groupedExperienceId: string) =>
    categoryTags.filter(
      (category) => category.tag.id.toString() === groupedExperienceId,
    )[0];

  return !loading && valueCategories ? (
    <List
      className="mobile-availability-autocomplete-options-root"
      subheader={<li />}
    >
      {valueCategories.map((option: CategoryType<ValueType>, groupIndex: number) => {
        // Compute flattened offset so result_index is consistent with desktop
        const flatOffset = valueCategories
          .slice(0, groupIndex)
          .reduce((acc, g) => acc + g.results.length, 0);

        return (
        <li key={`section-${option.category}`} className="section-header">
          <ul className="list-item">
            <ListSubheader className="group-header">
              {option.label}
            </ListSubheader>
            {option.results.map((item: ValueType, itemIndex: number) => {
              const selectionIsCategory =
                item.categoryLabel === Category.Categories.toString();

              return (
                <ListItem
                  key={`item-${item.id}`}
                  className="group-item-container"
                  onClick={() => {
                    onResultClicked?.(item.categoryLabel, flatOffset + itemIndex);
                    const selectionIsAttraction =
                      item.categoryLabel === Category.Attractions.toString();
                    if (selectionIsCategory) {
                      const categoryTag = getGroupedExperience(item.id);
                      if (categoryTag) {
                        setSelectedGroupedExperience(categoryTag);
                        setFilterTags?.([{ ...categoryTag.tag, kind: { ActivityTagKind: "Category" as const } }]);
                        onOptionSelect?.();
                      }
                    } else if (selectionIsAttraction) {
                      const attractionTag = attractionTags?.find(
                        (t) => t.tag.id.toString() === item.id
                      ) ?? {
                        tag: { id: parseInt(item.id, 10), name: item.label.replace(/ \(\d+\)$/, ""), kind: { ActivityTagKind: "Attraction" as const } },
                        count: 0,
                      };
                      setSelectedGroupedExperience(attractionTag);
                      setFilterTags?.([{ ...attractionTag.tag, kind: { ActivityTagKind: "Attraction" as const } }]);
                      onOptionSelect?.();
                    } else {
                      // setting into local storage the shop context for clicked experience
                      // to be used in the shop page
                      try {
                        localStorage.setItem(
                          `shop-context-${item.id}`,
                          JSON.stringify({
                            destinationId: location?.id
                              ? getSelectionId(location.id)
                              : "",
                            searchLocation: location?.label,
                          }),
                        );
                      } catch (e) {
                        Logger.debug(e);
                      }
                      history.push(
                        `${PATH_SHOP}${getExperiencesShopQuery(
                          { value: item.id.toString() },
                          fromDate,
                          untilDate,
                          location?.label,
                          isTravelApp,
                        )}`,
                      );
                    }
                  }}
                >
                  <Box
                    className={clsx("item-info", {
                      category: selectionIsCategory,
                    })}
                  >
                    <ListItemIcon className="group-item-icon">
                      <Icon
                        name={
                          selectionIsCategory
                            ? "navigation-icon"
                            : "experiences-icon"
                        }
                      />
                    </ListItemIcon>
                    <Box className="group-label-and-reviews-container">
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          className: "list-item-text",
                        }}
                        className="group-item-text"
                      />
                      {item.rating && (
                        <AvailabilityReviewRating
                          reviewCount={item.rating.numberOfReviews}
                          scaledScore={item.rating.reviewAverage}
                          shortReviews
                        />
                      )}
                    </Box>
                  </Box>
                  <FontAwesomeIcon icon={faChevronRight} />
                </ListItem>
              );
            })}
          </ul>
        </li>
        );
      })}
    </List>
  ) : (
    <Typography variant="body1" className="autocomplete-loading-text">
      Loading
    </Typography>
  );
};

interface IMobileKeywordSearchProps extends MobileKeywordSearchConnectorProps {
  setSelectedGroupedExperience: (option: ExperienceTagCount) => void;
  location: IResult | null;
}

export const MobileKeywordSearch = (props: IMobileKeywordSearchProps) => {
  const {
    value,
    fetchValueCategories,
    valueCategories,
    setSelectedGroupedExperience,
    loading,
    experiencesByCategory,
    categoryTags,
    fromDate,
    untilDate,
    location,
    attractionTags,
    setFilterTags,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = React.useState<string>(
    value ? value.label : "",
  );

  const { isTravelApp } = useIsTravelApp();

  const {
    resetAnalyticsRefs,
    trackSecondarySearchFocus,
    trackSecondarySearchTyped,
    trackSecondarySearchResultClicked,
  } = useSecondarySearchAnalytics();

  const onKeywordSearch = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setInputValue(event.target.value);
    if (event.target.value) {
      fetchValueCategories(event.target.value);
      trackSecondarySearchTyped();
    }
  };

  const onClearLocationField = () => {
    setInputValue("");
    fetchValueCategories("");
  };

  const options = useMemo(() => {
    if (valueCategories && valueCategories?.length > 0) {
      return valueCategories.map((category) => {
        const { results, label: categoryLabel } = category;
        const resultsWithCategory = results.map((result) => ({
          ...result,

          categoryLabel,
        }));
        return { ...category, results: resultsWithCategory };
      });
    }
    return [];
  }, [valueCategories]);

  return (
    <>
      <MobileSearchFieldButton
        value={SEARCH_BY}
        onClick={() => {
          setModalOpen(true);
          trackSecondarySearchFocus();
        }}
        icon={<Icon name={IconName.MagnifyingGlass} />}
      />
      <MobilePopoverCard
        open={modalOpen}
        className="mobile-availability-autocomplete-popup"
        onClose={() => {
          setModalOpen(false);
          resetAnalyticsRefs();
        }}
        headerElement={
          <Box className="modal-header-container">
            <Typography className="header-title">
              {SEARCH_EXPERIENCES}
            </Typography>
            <ActionLink
              className="modal-close-button"
              onClick={() => {
                setModalOpen(false);
                resetAnalyticsRefs();
              }}
              content={<CloseButtonIcon className="close-button-icon" />}
              label="Close"
              showTappableArea
            />
          </Box>
        }
        fullScreen
      >
        <Box className="mobile-availability-autocomplete">
          <Box className="mobile-availability-autocomplete-input-field">
            <Icon name={IconName.B2BMapPin} />
            <TextField
              className="availability-autocomplete"
              onChange={onKeywordSearch}
              value={inputValue}
              label={SEARCH_BY}
              autoFocus
              InputProps={{
                disableUnderline: true,
              }}
            />
            <FontAwesomeIcon
              className={clsx("icon", { hidden: !inputValue })}
              icon={faTimesCircle}
              onClick={onClearLocationField}
            />
          </Box>

          <MobileAvailabilityAutocompleteOptions
            valueCategories={
              inputValue || options.find((option) => option?.results.length > 0)
                ? options
                : []
            }
            categoryTags={categoryTags}
            setSelectedGroupedExperience={setSelectedGroupedExperience}
            fromDate={fromDate}
            untilDate={untilDate}
            loading={loading}
            experiencesByCategory={experiencesByCategory}
            location={location}
            isTravelApp={isTravelApp}
            attractionTags={attractionTags}
            setFilterTags={setFilterTags}
            onOptionSelect={() => {
              setModalOpen(false);
              resetAnalyticsRefs();
            }}
            onResultClicked={trackSecondarySearchResultClicked}
          />
        </Box>
      </MobilePopoverCard>
    </>
  );
};
